export const ROUTES = {
  // Public routes
  HOME: '/',
  ABOUT: '/about',
  BENEFITS: '/benefits',
  CONTACT: '/contact',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  
  // Authentication routes
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    VERIFY_EMAIL: '/verify-email',
    MFA_SETUP: '/mfa-setup',
  },
  
  // Main application routes
  DASHBOARD: '/dashboard',
  
  // Calculator routes
  CALCULATOR: {
    INDEX: '/calculator',
    WIZARD: '/calculator/wizard',
    RESULTS: '/calculator/results',
    SCENARIOS: '/calculator/scenarios',
    SCENARIO_DETAIL: (id: string) => `/calculator/scenarios/${id}`,
    HISTORY: '/calculator/history',
    COMPARE: '/calculator/compare',
  },
  
  // Chat routes
  CHAT: {
    INDEX: '/chat',
    CONVERSATION: (id: string) => `/chat/${id}`,
    NEW: '/chat/new',
    ARCHIVED: '/chat/archived',
    SETTINGS: '/chat/settings',
  },
  
  // Intelligence routes
  INTELLIGENCE: {
    INDEX: '/intelligence',
    INSIGHTS: '/intelligence/insights',
    OPPORTUNITIES: '/intelligence/opportunities',
    MONITORING: '/intelligence/monitoring',
    REPORTS: '/intelligence/reports',
    ANALYTICS: '/intelligence/analytics',
  },
  
  // Profile routes
  PROFILE: {
    INDEX: '/profile',
    PERSONAL: '/profile/personal',
    PREFERENCES: '/profile/preferences',
    SECURITY: '/profile/security',
    DOCUMENTS: '/profile/documents',
    VERIFICATION: '/profile/verification',
    ACCOUNT: '/profile/account',
  },
  
  // Privacy & consent routes
  PRIVACY_MANAGEMENT: {
    INDEX: '/privacy-management',
    CONSENT: '/privacy-management/consent',
    DATA_EXPORT: '/privacy-management/data-export',
    DATA_DELETION: '/privacy-management/data-deletion',
    GDPR_RIGHTS: '/privacy-management/gdpr-rights',
    SECURITY_SETTINGS: '/privacy-management/security',
  },
  
  // Admin routes (protected)
  ADMIN: {
    INDEX: '/admin',
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    USER_DETAIL: (id: string) => `/admin/users/${id}`,
    SETTINGS: '/admin/settings',
    SECURITY: '/admin/security',
    AUDIT_LOGS: '/admin/audit-logs',
    METRICS: '/admin/metrics',
    HEALTH: '/admin/health',
  },
  
  // Error routes
  ERROR: {
    NOT_FOUND: '/404',
    UNAUTHORIZED: '/401',
    FORBIDDEN: '/403',
    SERVER_ERROR: '/500',
    MAINTENANCE: '/maintenance',
  },
  
  // Help & support routes
  HELP: {
    INDEX: '/help',
    FAQ: '/help/faq',
    GUIDES: '/help/guides',
    CONTACT_SUPPORT: '/help/contact',
    ACCESSIBILITY: '/help/accessibility',
    TECHNICAL_ISSUES: '/help/technical',
  },
} as const;

// Navigation structure for main app
export const NAVIGATION = {
  MAIN: [
    {
      name: 'Dashboard',
      path: ROUTES.DASHBOARD,
      icon: 'Home',
      description: 'Your personal benefits overview',
    },
    {
      name: 'Calculator',
      path: ROUTES.CALCULATOR.INDEX,
      icon: 'Calculator',
      description: 'Calculate your benefit entitlements',
    },
    {
      name: 'AI Chat',
      path: ROUTES.CHAT.INDEX,
      icon: 'MessageCircle',
      description: 'Get personalized guidance from our AI assistant',
    },
    {
      name: 'Intelligence',
      path: ROUTES.INTELLIGENCE.INDEX,
      icon: 'Brain',
      description: 'Insights and opportunities for optimization',
    },
  ],
  SECONDARY: [
    {
      name: 'Profile',
      path: ROUTES.PROFILE.INDEX,
      icon: 'User',
      description: 'Manage your personal information',
    },
    {
      name: 'Privacy',
      path: ROUTES.PRIVACY_MANAGEMENT.INDEX,
      icon: 'Shield',
      description: 'Control your data and privacy settings',
    },
    {
      name: 'Help',
      path: ROUTES.HELP.INDEX,
      icon: 'HelpCircle',
      description: 'Get help and support',
    },
  ],
} as const;

// Breadcrumb configuration
export const BREADCRUMBS = {
  [ROUTES.DASHBOARD]: ['Dashboard'],
  [ROUTES.CALCULATOR.INDEX]: ['Calculator'],
  [ROUTES.CALCULATOR.WIZARD]: ['Calculator', 'Wizard'],
  [ROUTES.CALCULATOR.RESULTS]: ['Calculator', 'Results'],
  [ROUTES.CALCULATOR.SCENARIOS]: ['Calculator', 'Scenarios'],
  [ROUTES.CALCULATOR.HISTORY]: ['Calculator', 'History'],
  [ROUTES.CHAT.INDEX]: ['Chat'],
  [ROUTES.CHAT.NEW]: ['Chat', 'New Conversation'],
  [ROUTES.INTELLIGENCE.INDEX]: ['Intelligence'],
  [ROUTES.INTELLIGENCE.INSIGHTS]: ['Intelligence', 'Insights'],
  [ROUTES.INTELLIGENCE.OPPORTUNITIES]: ['Intelligence', 'Opportunities'],
  [ROUTES.PROFILE.INDEX]: ['Profile'],
  [ROUTES.PROFILE.PERSONAL]: ['Profile', 'Personal Information'],
  [ROUTES.PROFILE.PREFERENCES]: ['Profile', 'Preferences'],
  [ROUTES.PRIVACY_MANAGEMENT.INDEX]: ['Privacy Management'],
  [ROUTES.PRIVACY_MANAGEMENT.CONSENT]: ['Privacy Management', 'Consent'],
} as const;